import React, { useEffect } from 'react';
import axios from 'axios';
// @ts-ignore
import 'react-datepicker/dist/react-datepicker.css';
// @ts-ignore
import 'react-toastify/dist/ReactToastify.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PermissionsProvider } from './context/PermissionsContext';
import Register from './components/Register';
import Login from './components/LoginForm';
import Dashboard from './components/DashboardForm';
import PrivateRoute from './components/PrivateRoute';
import Instructions from './components/InstructionsPage';
import Clients from './components/clients';
import ForgotPassword from './components/ForgotPassword';
import Personal from './components/Personal';
import Commercial from './components/Commercial';
import Medical from './components/Medical';
import { ToastContainer } from 'react-toastify';
import Employees from './components/employees';
import EmployeeDetail from './components/EmployeeDetail';
import CreateQuotePage from './components/CreateQuotePage';
import Administrators from './components/Administrators';
import AdministratorDetail from './components/AdministratorDetail';
import AdministratorEdit from './components/AdministratorEdit';
import Underwriters from './components/Underwriters';
import UnderwriterDetails from './components/UnderwriterDetails';
import ConsultantsPage from './components/ConsultantsPage';
import ConsultantDetails from './components/ConsultantDetails';
import QuoteDetail from './components/QuoteDetail';
import QuoteDetailPage from './components/QuoteDetail';
import ProcessDefinitions from './components/ProcessDefinitions';
import EmailSettingsPage from './components/EmailSettingsPage';
import Settings from './components/Settings';
import QuotationDeclarations from './components/QuotationDeclarations';
import ApplicationInsurers from './components/ApplicationInsurers';
import ApplicationCategories from './components/ApplicationCategories';
import ApplicationQuestions from './components/ApplicationQuestions';
import ProposalApplicationForm from './components/ProposalApplicationForm';
import ProposalPrint from './components/ProposalPrint';
import PublicSignaturePage from './components/PublicSignaturePage'
import SignatureComplete from './components/SignatureComplete';
import UsersPage from './components/UsersPage';
import ProcessDefinitionsInstructions from './components/ProcessDefinitionsInstructions';
import ProcessDefinitionsPolicies from './components/ProcessDefinitionsPolicies';
import AddClientPage from './components/AddClientPage';
import AddPersonPage from './components/AddPersonPage';
import AddPreviousInsurancePage from './components/AddPreviousInsurancePage';
import AddPreviousClaimPage from './components/AddPreviousClaimPage';
import AddNotePage from './components/AddNotePage';
import AddFilePage from './components/AddFilePage';
import AddPropertyPage from './components/AddPropertyPage';
import AddHouseholdContentsPage from './components/AddHouseholdContentsPage';
import AddVehiclePage from './components/AddVehiclePage';
import AddTrailerCaravanPage from './components/AddTrailerCaravanPage';
import AddRiskItemPage from './components/AddRiskItemPage';
import AddValueAddedPage from './components/AddValueAddedPage';
import AddWaterCraftPage from './components/AddWaterCraftPage';
import CreatePolicy from './components/CreatePolicy';
import PolicyDetail from './components/PolicyDetail';
import CreateClaim from './components/CreateClaim';
import ReturnDebitsProcessDefinition from './components/ReturnDebitsProcessDefinition';
import ManageClaims from './components/ManageClaims';
import ManageQuotations from './components/ManageQuotations';
import ManageRenewals from './components/ManageRenewals';
import ManageReturnDebits from './components/ManageReturnDebits';
import FileViewer from './components/FileViewer';
import { tokenService } from './services/tokenService';
import Reports from './components/Reports';
import MasterBrokerPage from './components/MasterBrokerPage';
import ClaimsIntake from './components/ClaimsIntake';
import PermissionsAdmin from './components/PermissionsAdmin';

// Keep this - it sets the base URL for axios
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Configure axios globally
axios.defaults.baseURL = API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add token interceptor
axios.interceptors.request.use(
  (config) => {
    const token = tokenService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function App() {
  useEffect(() => {
    const sendHeartbeat = async () => {
      try {
        const token = tokenService.getToken();
        if (token) {
          // ✅ CHANGE THIS - Use relative path, axios will use baseURL
          await axios.post('/auth/heartbeat', {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      } catch (error) {
        console.error('Heartbeat error:', error);
      }
    };

    const interval = setInterval(sendHeartbeat, 60000); // Every 60 seconds
    sendHeartbeat(); // Send immediately

    return () => clearInterval(interval);
  }, []);

  return (
    <AuthProvider>
      <PermissionsProvider>
    <Router>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/personal/:id" element={<Personal />} />
        <Route path="/commercial/:clientId" element={<Commercial />} />
        <Route path="/medical/:clientId" element={<Medical />} />
        <Route path="/employees" element={<Employees />} />
        <Route path="/employees/:id" element={<EmployeeDetail />} />
        <Route path="/create-quote/:clientId" element={<CreateQuotePage />} />
        <Route path="/insurers/administrators" element={<Administrators />} />
        <Route path="/insurers/administrators/:id" element={<AdministratorDetail />} />
        <Route path="/administrators/:id/edit" element={<AdministratorEdit />} />
        <Route path="/underwriters" element={<Underwriters />} />
        <Route path="/underwriters/:underwriterId" element={<UnderwriterDetails />} />
        <Route path="/consultants" element={<ConsultantsPage />} />
        <Route path="/consultants/:id" element={<ConsultantDetails />} />
        <Route path="/quotes/master/:clientId/:quoteId" element={<QuoteDetailPage />} />
        <Route path="/quotes/:id" element={<QuoteDetail />} />
        <Route path="/process-definitions" element={<ProcessDefinitions />} />
        <Route path="/email-settings" element={<EmailSettingsPage />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/process-definitions/quotation-declarations" element={<QuotationDeclarations />} />
        <Route path="/application-insurers" element={<ApplicationInsurers />} />
        <Route path="/application-categories" element={<ApplicationCategories />} />
        <Route path="/application-questions" element={<ApplicationQuestions />} />
        <Route path="/proposal-form/:quoteId" element={<ProposalApplicationForm />} />
        <Route path="/proposal/:quoteId/print" element={<ProposalPrint />} />
        <Route path="/sign/:token" element={<PublicSignaturePage />} />
        <Route path="/signature-complete" element={<SignatureComplete />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/add-client" element={<AddClientPage />} />
        <Route path="/add-person/:clientId" element={<AddPersonPage />} />
        <Route path="/add-previous-insurance/:quoteId" element={<AddPreviousInsurancePage />} />
        <Route path="/add-previous-claim/:quoteId" element={<AddPreviousClaimPage />} />
        <Route path="/add-note/:quoteId" element={<AddNotePage />} />
        <Route path="/add-file" element={<AddFilePage />} />
        <Route path="/add-property/:quoteId" element={<AddPropertyPage />} />
        <Route path="/add-household-contents/:quoteId" element={<AddHouseholdContentsPage />} />
        <Route path="/add-vehicle/:quoteId" element={<AddVehiclePage />} />
        <Route path="/add-trailer-caravan/:quoteId" element={<AddTrailerCaravanPage />} />
        <Route path="/add-risk-item/:quoteId" element={<AddRiskItemPage />} />
        <Route path="/add-value-added/:quoteId" element={<AddValueAddedPage />} />
        <Route path="/add-water-craft/:quoteId" element={<AddWaterCraftPage />} />
        <Route path="/create-policy/:clientId" element={<CreatePolicy />} />
        <Route path="/policy/:policyId" element={<PolicyDetail />} />
        <Route path="/process-definitions/instructions" element={<ProcessDefinitionsInstructions />} />
        <Route path="/process-definitions/policies" element={<ProcessDefinitionsPolicies />} />
        <Route path="/process-definitions/return-debits" element={<ReturnDebitsProcessDefinition />} />
        <Route path="/create-claim/:clientId" element={<CreateClaim />} />
        <Route path="/claims" element={<ManageClaims />} />
        <Route path="/quotations" element={<ManageQuotations />} />
        <Route path="/renewals" element={<ManageRenewals />} />
        <Route path="/return-debits" element={<ManageReturnDebits />} />
        <Route path="/file-viewer/:fileId" element={<FileViewer />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/master-brokers" element={<MasterBrokerPage />} />
        <Route path="/claims-intake" element={<ClaimsIntake />} />
        <Route path="/admin/permissions" element={<PermissionsAdmin />} />
        
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/instructions"
          element={
            <PrivateRoute>
              <Instructions />
            </PrivateRoute>
          }
        />
        <Route path="/" element={<Login />} />
      </Routes>
    </Router>
    </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;